/**
 * Klaviyo Integration Module (Story 12.7)
 *
 * Barrel export for all Klaviyo OAuth, discovery, error, API, and adapter modules.
 *
 * Key differences from Mailchimp:
 * - PKCE required (code_challenge + code_verifier)
 * - Scopes required (accounts:read, metrics:read, etc.)
 * - Refresh tokens supported (access tokens expire)
 * - API revision header required on all calls
 */

import { registerAdapter } from "../registry";
import { klaviyoAdapter } from "./klaviyo";

// Register Klaviyo adapter (Story 12.7)
registerAdapter("klaviyo", klaviyoAdapter);

// Platform values (shared constant for frontend/backend alignment)
export const KLAVIYO_PLATFORM_VALUES = ["klaviyo"] as const;
export type KlaviyoPlatform = (typeof KLAVIYO_PLATFORM_VALUES)[number];

// OAuth utilities
export {
  buildKlaviyoAuthUrl,
  exchangeKlaviyoCode,
  refreshKlaviyoToken,
  parseKlaviyoCallbackError,
  type KlaviyoAuthUrlOptions,
  type KlaviyoTokenResponse,
  type KlaviyoCallbackError,
} from "./oauth";

// API helpers
export {
  buildKlaviyoHeaders,
  fetchMetricsList,
  fetchMetricAggregates,
  fetchKlaviyoAccounts,
  fetchKlaviyoAccountsWithDetail,
  // Story 12.8: Reporting API helpers
  fetchMappedMetric,
  fetchCampaignValuesReport,
  fetchFlowValuesReport,
  fetchFormValuesReport,
  fetchSegmentValuesReport,
  // Story 12.8: Quota tracking helpers + constants
  getReportingQuotaUsage,
  incrementReportingQuota,
  checkReportingQuotaBudget,
  KLAVIYO_REPORTING_QUOTA_HARD_LIMIT,
  KLAVIYO_REPORTING_QUOTA_WARN_THRESHOLD,
  KLAVIYO_REPORTING_QUOTA_ACTUAL_CAP,
  // Story 12.8: Statistics lists
  KLAVIYO_CAMPAIGN_STATISTICS,
  KLAVIYO_FLOW_STATISTICS,
  KLAVIYO_FORM_STATISTICS,
  KLAVIYO_SEGMENT_STATISTICS,
  // Story IMC-25: Tags, Campaign Messages, Segment Profile Count
  fetchAllTags,
  fetchTagsForCampaign,
  fetchCampaignsWithMessages,
  fetchSegmentProfileCount,
  type KlaviyoAccountInfo,
  type KlaviyoAccountResult,
  type KlaviyoMetricAggregateResult,
  type KlaviyoCampaignReportEntry,
  type KlaviyoFlowReportEntry,
  type KlaviyoFormReportEntry,
  type KlaviyoSegmentReportEntry,
  type KlaviyoCampaignGroupings,
  type KlaviyoFlowGroupings,
  type KlaviyoFormGroupings,
  type KlaviyoSegmentGroupings,
  type KlaviyoTag,
  type KlaviyoCampaignMeta,
  type KlaviyoCampaignMessage,
} from "./api";

// List & Segment Discovery
export {
  fetchKlaviyoLists,
  fetchKlaviyoSegments,
  type KlaviyoList,
  type KlaviyoSegment,
} from "./discovery";

// Error parsing
export {
  parseKlaviyoApiError,
  KlaviyoQuotaExhaustedError,
  type KlaviyoApiError,
  type KlaviyoErrorType,
} from "./errors";

// Klaviyo Adapter (Story 12.7)
export {
  klaviyoAdapter,
  KLAVIYO_METRICS,
  KLAVIYO_DIMENSIONS,
} from "./klaviyo";
